import type { App } from 'vue'
import { createRouter, createWebHistory, type Router, type RouteRecordRaw } from 'vue-router'
import axios from 'axios'
import { useRouteGuards } from '@fusion/auth-v3'
import { createFusionRoutes, type FusionRoutesOptions } from '@fusion/saas-components-v3'

import HomePublic from '@/views/home-public.vue'
import HomePrivate from '@/views/home-private.vue'
import Dashboard from '@/views/dashboard.vue'
import AlertHistory from '@/views/alert-history.vue'
import ManageDevices from '@/views/manage-devices.vue'
import ManageSubscriptions from '@/views/manage-subscriptions.vue'
import HelpCenter from '@/views/help-center.vue'
import NonVerified from '@/views/non-verified.vue'
import ReleaseNotes from '@/views/release-notes.vue'
import API from '@/views/api.vue'
import AdministrationPanel from '@/views/admin-panel.vue'
import DeveloperPanel from '@/views/developer-panel.vue'
import StudyManager from '@/views/study-manager.vue'

let router: Router

/**
 * Register all navigation components required to properly render the router.
 * @param app The Vue application instance.
 * @returns
 */
const registerComponents = (app: App) => {
  app.component('HomePublic', HomePublic)
  app.component('Dashboard', Dashboard)
  app.component('AlertHistory', AlertHistory)
  app.component('ManageDevices', ManageDevices)
  app.component('ManageSubscriptions', ManageSubscriptions)
  app.component('HelpCenter', HelpCenter)
  app.component('HomePrivate', HomePrivate)
  app.component('NonVerified', NonVerified)
  app.component('ReleaseNotes', ReleaseNotes)
  app.component('API', API)
  app.component('AdministrationPanel', AdministrationPanel)
  app.component('DeveloperPanel', DeveloperPanel)
  app.component('StudyManager', StudyManager)
}

/**
 * Gets the router dynamically using data from the database.
 * @param app The Vue application instance.
 * @returns The Vue Router instance
 */
const initializeRouter = async (app: App): Promise<Router> => {
  const applicationId = APP_ENV.VITE_APP_APPLICATION_ID

  try {
    const { data } = await axios.get(
      `${APP_ENV.VITE_APP_APPLICATIONS_HOST}/applications/${applicationId}/navigation`
    )
    const navRoutes = data.map((route: RouteRecordRaw) => {
      return route
    })

    registerComponents(app)
    const options: FusionRoutesOptions = {
      app,
      navRoutes,
      authGuards: useRouteGuards()
    }
    router = createRouter({
      history: createWebHistory(APP_ENV.VITE_APP_PUBLIC_PATH),
      routes: createFusionRoutes(options)
    })
    return router
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Router initialization failed:', error)
    throw error
  }
}

/**
 * Ensure the router is created only once.
 * @param app The Vue application instance.
 * @returns The Vue Router instance
 */
export const getRouter = async (app: App): Promise<Router> => {
  if (router) {
    return router
  }
  return initializeRouter(app)
}
